'use client';

import { useState, useRef, useCallback } from 'react';
import type { ScrapedProduct } from '@/lib/types';
import type { ChatMessage } from '@/lib/chat/types';
import type { ChatMessageUI } from '../types';
import { useClientConfig } from '@/lib/clients/ClientContext';

let messageIdCounter = 0;
function generateId() {
  return `msg-${Date.now()}-${++messageIdCounter}`;
}

export function useChat() {
  const clientConfig = useClientConfig();
  // UI messages (what the user sees)
  const [messages, setMessages] = useState<ChatMessageUI[]>([]);
  // Full API messages (OpenAI format, includes tool call memory)
  const apiMessagesRef = useRef<ChatMessage[]>([]);
  // Products from the latest _meiliSearchSources
  const [products, setProducts] = useState<ScrapedProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchProgress, setSearchProgress] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isLoading) return;

    // Abort any in-flight request
    abortRef.current?.abort();
    const abortController = new AbortController();
    abortRef.current = abortController;

    // Add user message
    const userMsg: ChatMessageUI = {
      id: generateId(),
      role: 'user',
      content: text.trim(),
      timestamp: Date.now(),
    };
    const assistantMsg: ChatMessageUI = {
      id: generateId(),
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      isStreaming: true,
    };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    apiMessagesRef.current = [
      ...apiMessagesRef.current,
      { role: 'user', content: text.trim() },
    ];

    setIsLoading(true);
    setSearchProgress(null);

    try {
      const res = await fetch(`${clientConfig.apiOrigin ?? ''}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessagesRef.current }),
        signal: abortController.signal,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${res.status}`);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error('No response stream');

      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;
          if (trimmed === 'data: [DONE]') continue;

          try {
            const event = JSON.parse(trimmed.slice(6));

            switch (event.type) {
              case 'text-delta':
                fullContent += event.content;
                setMessages(prev =>
                  prev.map(m =>
                    m.id === assistantMsg.id
                      ? { ...m, content: fullContent }
                      : m
                  )
                );
                break;

              case 'sources':
                if (Array.isArray(event.products) && event.products.length > 0) {
                  setProducts(event.products);
                }
                break;

              case 'search-progress':
                setSearchProgress(event.query || null);
                break;

              case 'append-message':
                if (event.message) {
                  apiMessagesRef.current = [
                    ...apiMessagesRef.current,
                    event.message,
                  ];
                }
                break;

              case 'error':
                fullContent += `\n\n_Error: ${event.message}_`;
                setMessages(prev =>
                  prev.map(m =>
                    m.id === assistantMsg.id
                      ? { ...m, content: fullContent, isStreaming: false }
                      : m
                  )
                );
                break;

              case 'done':
                break;
            }
          } catch {
            // Skip malformed lines
          }
        }
      }

      // Finalize: mark as not streaming, add to API messages
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMsg.id
            ? { ...m, isStreaming: false }
            : m
        )
      );

      if (fullContent) {
        apiMessagesRef.current = [
          ...apiMessagesRef.current,
          { role: 'assistant', content: fullContent },
        ];
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;

      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMsg.id
            ? {
                ...m,
                content: `Σφάλμα: ${(err as Error).message}`,
                isStreaming: false,
              }
            : m
        )
      );
    } finally {
      setIsLoading(false);
      setSearchProgress(null);
    }
  }, [isLoading]);

  const clearChat = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setProducts([]);
    setIsLoading(false);
    setSearchProgress(null);
    apiMessagesRef.current = [];
  }, []);

  return {
    messages,
    products,
    isLoading,
    searchProgress,
    sendMessage,
    clearChat,
  };
}
